// ===================================
// LOGIN PAGE - src/pages/Login.tsx
// ===================================
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import bgPattern from "../assets/pattern-square.svg";
import Logo from "../assets/logo.png";
import { Loader2 } from "lucide-react";
import { notifyError, notifySuccess } from "../utils/Toastify";

const SkeletonBox = ({ className }: { className?: string }) => (
  <div className={`bg-gray-700 animate-pulse rounded ${className}`} />
);

const aesSalt = process.env.REACT_APP_AES_SALT as string;
const singleLoginBEUrl = process.env.REACT_APP_SINGLELOGIN_BE_REDIRECT_URL;
const moduleLink = process.env.REACT_APP_FE_MODULE_URL;
const singleLoginRedirectUrl =
  process.env.REACT_APP_SINGLELOGIN_FE_REDIRECT_URL;
const redirectLink = process.env.REACT_APP_REDIRECT_URL;

const encryptPassword = (plaintext: string) => {
  const password = CryptoJS.AES.encrypt(plaintext, aesSalt).toString();
  return password;
};

type Business = {
  id: string;
  name: string;
  email: string;
};

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [showBusinessList, setShowBusinessList] = useState(false);
  const { verifyUserApi, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleMessage = async (event: any) => {
      try {
        if (!singleLoginRedirectUrl) {
          return;
        }

        let expectedOrigin;
        try {
          const urlObj = new URL(singleLoginRedirectUrl);
          expectedOrigin = urlObj.origin;
        } catch (error) {
          return;
        }

        if (event.origin !== expectedOrigin) {
          return;
        }

        let data;
        if (typeof event.data === "string") {
          try {
            data = JSON.parse(event.data);
          } catch {
            data = undefined;
          }
        } else if (typeof event.data === "object") {
          data = event.data;
        } else {
          return;
        }

        if (data?.token) {
          localStorage.setItem("token", data.token);
        }

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        // Make the API call to get module authentication token
        // const response = await fetch(`${singleLoginBEUrl}/users/module-auth`, {
        //   method: "POST",
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //     "Content-Type": "application/json",
        //   },
        //   body: JSON.stringify({
        //     moduleUrl: moduleLink,
        //     businessId: data.business_id,
        //   }),
        // });

        // const responseData = await response.json();

        // // Get the module auth token from response
        // const moduleAuthToken = responseData.token;

        // if (!moduleAuthToken) {
        //   throw new Error("No token received from module authentication");
        // }
        const response_ = await verifyUserApi(token);
        notifySuccess("You have successfully logged in.");
      } catch (e) {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  // useLayoutEffect(() => {
  //   const isPopup = window.opener !== null;
  //   const trustedHostEnv = process.env.REACT_APP_SINGLELOGIN_HOST || "";
  //   const trustedReferrerEnv = process.env.REACT_APP_TRUSTED_REFERRER || "";

  //   const trustedHost = trustedHostEnv.trim().toLowerCase();
  //   const currentHost = (window.location.host || "").trim().toLowerCase();

  //   let referrerHost = "";
  //   if (document.referrer) {
  //     try {
  //       referrerHost = new URL(document.referrer).host.trim().toLowerCase();
  //     } catch (_) {}
  //   }

  //   const isTrustedCurrentHost =
  //     Boolean(trustedHost) && currentHost === trustedHost;

  //   let isTrustedReferrer = false;
  //   if (trustedReferrerEnv) {
  //     try {
  //       const trustedRefOrigin = new URL(trustedReferrerEnv).host
  //         .trim()
  //         .toLowerCase();
  //       isTrustedReferrer = referrerHost === trustedRefOrigin;
  //     } catch (_) {
  //       isTrustedReferrer =
  //         referrerHost === trustedReferrerEnv.trim().toLowerCase() ||
  //         (document.referrer || "")
  //           .toLowerCase()
  //           .includes(trustedReferrerEnv.trim().toLowerCase());
  //     }
  //   } else if (trustedHost) {
  //     isTrustedReferrer = referrerHost === trustedHost;
  //   }

  //   if (isPopup && (isTrustedCurrentHost || isTrustedReferrer)) {
  //     setIsLoading(true);
  //   }
  // }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const [idToken, businessesList] = await loginWithEmailPassword(
        username,
        password
      );
      if (idToken) {
        localStorage.setItem("token", idToken);
      }
      setBusinesses(businessesList);
      setShowBusinessList(true);
      // await login(username, password);
      // addNotification({
      //   type: "success",
      //   title: "Welcome back!",
      //   message: "You have successfully logged in.",
      // });
    } catch (error: any) {
      notifyError(
        error.response?.data?.error?.message || "Invalid credentials"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const demoUsers = [
    {
      username: "sahmie",
      role: "Owner (Admin)",
      business: "Pridict Ltd",
      description: "Full administrative access to all features",
    },
    {
      username: "john_manager",
      role: "Manager",
      business: "Pridict Ltd",
      description: "Department management and document access",
    },
    {
      username: "jane_user",
      role: "User",
      business: "Pridict Ltd",
      description: "Standard user with limited permissions",
    },
    {
      username: "alice_acme",
      role: "Owner (Admin)",
      business: "ACME Corp",
      description: "Admin for different business tenant",
    },
  ];

  const loginWithEmailPassword = async (email: string, password: string) => {
    try {
      // Encrypt the password before sending
      const encryptedPassword = encryptPassword(password);

      const response = await fetch(
        `${singleLoginBEUrl}/users/login-with-email-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            password: encryptedPassword,
            moduleUrl: moduleLink,
          }),
        }
      );

      // if (!response.ok) {
      //   const errorData = await response.json().catch(() => ({}));
      //   throw new Error(errorData?.message || `Authentication failed with status: ${response.status}`);
      // }

      const data = await response.json();

      // Check if businesses are returned in the response
      if (data.businessesList) {
        return [data.idToken, data.businessesList];
      } else {
        throw new Error("No businesses received from server");
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBusinessSelect = async (business: Business) => {
    try {
      setIsLoading(true);

      // Get the token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Make the API call to get module authentication token
      const response = await fetch(`${singleLoginBEUrl}/users/module-auth`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          moduleUrl: moduleLink,
          businessId: business.id,
        }),
      });

      // if (!response.ok) {
      //   const errorData = await response.json().catch(() => ({}));
      //   throw new Error(errorData?.message || `Module authentication failed with status: ${response.status}`);
      // }

      const data = await response.json();

      // Get the module auth token from response
      const moduleAuthToken = data.token;
      localStorage.setItem("verify-chatbot-token", moduleAuthToken);

      if (!moduleAuthToken) {
        throw new Error("No token received from module authentication");
      }
      const verifyResponse = await verifyUserApi(data.token);
      localStorage.setItem("token", verifyResponse.access_token);

      // Store the new token in localStorage
      // localStorage.setItem("token", moduleAuthToken);
      notifySuccess("You have successfully logged in.");
      // Navigate to book list
      // navigate("/book-list");
    } catch (error) {
      console.error("Business selection failed:", error);
      // alert(`❌ Failed to authenticate with business: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSingleLoginRedirect = () => {
    window.open(
      `${singleLoginRedirectUrl}/?moduleurl=${moduleLink}&redirecturl=${redirectLink}`,
      "SingleLoginPopup",
      "width=500,height=600,left=0,top=0,resizable,scrollbars"
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#020617] py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${bgPattern})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
      }}
    >
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={Logo} alt="" className="w-[70px] mx-auto" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#fff]">
            MicroAgent Chatbot
          </h2>
          <p className="mt-2 text-center text-sm text-[#64748b]">
            Sign in to access your AI-powered document assistant
          </p>
        </div>

        <div
          className=" rounded-lg shadow-lg p-8 border-[#334155] border"
          style={{ backgroundColor: "#1e293b" }}
        >
          {!showBusinessList ? (
            <>
              {" "}
              <form className="space-y-6 " onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-[#64748b] mb-2"
                  >
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2 pr-10"
                    style={{ backgroundColor: "#020617" }}
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-[#64748b] mb-2"
                  >
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2 pr-10"
                    style={{ backgroundColor: "#020617" }}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !username || !password}
                  className="w-full py-2 px-4 rounded-lg text-white font-semibold transition-colors duration-200 disabled:opacity-50 flex items-center justify-center"
                  style={{ backgroundColor: "#8b3dff" }}
                >
                  {isLoading ? (
                    <Loader2 className="text-white animate-spin" />
                  ) : (
                    "Sign in"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSingleLoginRedirect}
                  className="w-full py-2 px-4 rounded-lg text-white font-semibold"
                  style={{ backgroundColor: "#64748b" }}
                >
                  Sign in with SingleLogin.io
                </button>
              </form>
            </>
          ) : (
            <div>
              {isLoading ? (
                // Show skeleton placeholders
                Array.from({ length: businesses?.length }).map((_, idx) => (
                  <div
                    key={idx}
                    className="border border-[#334155] rounded-lg p-2  mb-2"
                    style={{ backgroundColor: "#020617" }}
                  >
                    <div className="flex items-start justify-between ">
                      <div className="flex flex-col gap-1 items-start">
                        <SkeletonBox className="h-6 w-32" />
                        <SkeletonBox className="h-3 w-40" />
                      </div>
                      <SkeletonBox className="h-3 w-32" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="mt-3">
                  <h2 className="text-xl font-semibold text-white text-center mb-6">
                    Select Your Business
                  </h2>

                  <div className="mt-6 space-y-3">
                    {showBusinessList &&
                      businesses.map((business) => (
                        <button
                          key={business.id}
                          // type="button"
                          onClick={() => {
                            setUsername(business.name);
                            setPassword("password123");
                          }}
                          className="w-full text-left px-4 py-3 border border-[#334155] rounded-lg bg-[#020617] focus:outline-none focus:ring-2 focus:ring-[#8b3dff] transition-colors"
                          // disabled={isLoading}
                        >
                          <div
                            className="flex justify-between items-start"
                            onClick={() => handleBusinessSelect(business)}
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-white">
                                {business.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {business.email}
                              </div>
                              {/* <div className="text-xs text-gray-400 mt-1">
                          {business.email}
                        </div> */}
                            </div>
                            <div className="text-xs text-[#8b3dff] font-medium">
                              {business.name}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>

                  {/* <p className="mt-4 text-xs text-gray-500 text-center">
              💡 Click any demo user to auto-fill credentials
              <br />
              🔑 Password:{" "}
              <code className="bg-gray-100 px-1 rounded">password123</code>
            </p> */}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
